import React, { useContext, useEffect, useState } from 'react';
import './OrdersList.css';
import { getOrdersByYear, getPaidForCustomerIds } from '../../services/orderService';
import { useHistory } from 'react-router-dom';
import { LOGIN_ROUTE } from '../../routes';
import { Icon, Button } from 'semantic-ui-react';
import { CartContext, SHALOCH_CUSTOMER_IDS } from '../../contexts/CartProvider';

const extractOrderFields = (order) => {
    const date = order.date || order.createdAt || order.created || order.orderDate || '';
    const recipientId = order.toCustomerId || order.recipientId || order.toCustomer || order.customerId || null;
    const recipientName =
      order.toCustomerName ||
      order.recipientName ||
      order.customerName ||
      order.toName ||
      '';
    return { date, recipientId, recipientName };
  };

const OrdersList = ({ year }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selections, setSelections] = useState({});
  const [paidThisYear, setPaidThisYear] = useState([]);
  const history = useHistory();
  const { updateCart, cart, paidForCustomers } = useContext(CartContext);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!year) return;
      setLoading(true);
      try {
        const data = await getOrdersByYear(year);
        if (mounted) setOrders(Array.isArray(data) ? data : []);
      } catch (e) {
        if (mounted) setOrders([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => (mounted = false);
  }, [year]);

  // fetch current year's paid orders to know if items are "ordered and paid"
  useEffect(() => {
    let mounted = true;
    const loadPaidThisYear = async () => {
      try {
        const thisYear = new Date().getFullYear();
        const paid = await getPaidForCustomerIds(thisYear);
        if (mounted) setPaidThisYear(paid || []);
      } catch (e) {
        if (mounted) setPaidThisYear([]);
      }
    };

    loadPaidThisYear();
    return () => (mounted = false);
  }, []);

  // initialize selections from cart
  useEffect(() => {
    if (cart && cart[SHALOCH_CUSTOMER_IDS]) {
      const cartIds = cart[SHALOCH_CUSTOMER_IDS];
      const newSelections = {};
      cartIds.forEach((id) => {
        newSelections[id] = true;
      });
      setSelections(newSelections);
    }
  }, [cart]);

  if (!year) return <div className="orders-list">Please select a year to view orders.</div>;
  if (loading) return <div className="orders-list">Loading orders...</div>;

  return (
    <div className="orders-list">
      <h2>Your Orders — {year}</h2>
      {orders && orders.length > 0 ? (
        <div>
          <div className="orders-table-wrap">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Created Date</th>
                  <th>Reciprocated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(
                  // build rows from order payload
                  orders
                    .map((order) => {
                      const { date, recipientId, recipientName } = extractOrderFields(order);
                      const name = recipientName || '-';
                      return { order, recipientId, date, name };
                    })
                    .map((row, idx) => {
                      const humanDate = row.date ? new Date(row.date).toLocaleDateString() : '-';
                      const recipReciprocated = Boolean(row.order?.reciprocation || row.order?.reciprocated);
                      const recipientId = row.recipientId;
                      const paidFor = paidThisYear && paidThisYear.includes(recipientId);
                      const isSelected = selections[recipientId];
                      const handleSelect = (value) => {
                        setSelections((prevSelections) => {
                          const updated = {
                            ...prevSelections,
                            [recipientId]: value,
                          };
                          
                          // immediately update cart with current selection state
                          const selectedIds = Object.entries(updated).reduce(
                            (accum, [key, val]) => {
                              if (val) {
                                return accum.concat(Number.parseInt(key, 10));
                              }
                              return accum;
                            },
                            []
                          );
                          updateCart(SHALOCH_CUSTOMER_IDS, selectedIds);
                          
                          return updated;
                        });
                      };

                      if (paidFor) {
                        return (
                          <tr key={row.order.id || row.order.orderId || idx} className="order-row">
                            <td>{row.name}</td>
                            <td>{humanDate}</td>
                            <td>
                              {recipReciprocated ? (
                                <Icon name="check circle" color="green" />
                              ) : (
                                <Icon name="circle" color="grey" />
                              )}
                            </td>
                            <td>
                              <span className="ordered-and-paid">ordered and paid</span>
                            </td>
                          </tr>
                        );
                      }

                      return (
                        <tr key={row.order.id || row.order.orderId || idx} className="order-row">
                          <td>{row.name}</td>
                          <td>{humanDate}</td>
                          <td>
                            {recipReciprocated ? (
                              <Icon name="check circle" color="green" />
                            ) : (
                              <Icon name="circle" color="grey" />
                            )}
                          </td>
                          <td>
                            {isSelected ? (
                              <Button
                                className="btn-remove-from-cart"
                                onClick={() => handleSelect(false)}
                                basic
                                color="red"
                              >
                                <Icon name="minus circle" />
                                Added to Cart
                              </Button>
                            ) : (
                              <Button
                                className="btn-add-to-cart"
                                basic
                                color="blue"
                                onClick={() => handleSelect(true)}
                              >
                                <Icon name="plus circle" />
                                Add to Cart
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16 }}>
            <button
              className="register-account"
              onClick={() => history.push(LOGIN_ROUTE)}
            >
              Proceed to Order
            </button>
          </div>
        </div>
      ) : (
        <div className="no-orders">No orders for {year}.</div>
      )}
    </div>
  );
};

export default OrdersList;